import { Components, Theme } from '@mui/material'

const loadingIndicatorSelector = '&.MuiButton-loadingPositionCenter .MuiButton-loadingIndicator'

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    outlined: false
  }
}

export const primaryContainedButtonStyle = (theme: Theme) => ({
  backgroundColor: theme.palette.bg.brand.default,
  color: theme.palette.text.brand.onBrand,
  '&:hover': {
    backgroundColor: theme.palette.bg.brand.hover,
  },
  [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
    backgroundColor: theme.palette.bg.brand.default,
    color: theme.palette.text.brand.onBrand,
  },
})

export const secondaryContainedButtonStyle = (theme: Theme) => ({
  backgroundColor: theme.palette.bg.surface.elevated,
  borderStyle: 'solid',
  borderWidth: theme.strokeWidth.default,
  borderColor: theme.palette.border.subtle,
  color: theme.palette.text.muted,
  boxShadow: theme.customShadows.buttonDrop,
  '&:hover': {
    backgroundColor: theme.palette.bg.surface.elevated,
    color: theme.palette.text.default,
    borderColor: theme.palette.border.strong,
    boxShadow: [theme.customShadows.buttonInner, theme.customShadows.buttonDrop].join(', '),
  },
  [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
    backgroundColor: theme.palette.bg.surface.elevated,
    color: theme.palette.text.default,
    boxShadow: [theme.customShadows.buttonInner, theme.customShadows.buttonDrop].join(', '),
  },
})

export const MuiButton: Components<Omit<Theme, 'components'>>['MuiButton'] = {
  defaultProps: {
    disableRipple: true,
    disableElevation: true,
    loadingPosition: 'center',
  },
  styleOverrides: {
    root: ({ theme }) => ({
      '&.Mui-disabled': {
        opacity: 0.4,
      },
      '&.Mui-disabled.MuiButton-loading.MuiButton-loadingPositionCenter': {
        color: 'transparent',
      },
      ...theme.typography['body-medium'],
      textTransform: 'none',
    }),
    startIcon: ({ theme }) => ({
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(-1),
      '& > *:nth-of-type(1)': {
        fontSize: 18,
      },
    }),
    endIcon: ({ theme }) => ({
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(-1),
      '& > *:nth-of-type(1)': {
        fontSize: 18,
      },
    }),
  },
  variants: [
    {
      props: { variant: 'text' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.invisible,
      }),
    },
    // Colour
    {
      props: { color: 'primary', variant: 'contained' },
      style: ({ theme }) => primaryContainedButtonStyle(theme),
    },
    {
      props: { color: 'primary', variant: 'text' },
      style: ({ theme }) => ({
        color: theme.palette.text.brand.default,
        '&:hover': {
          backgroundColor: theme.palette.bg.brand.muted,
          color: theme.palette.text.brand.onBrandMuted,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.text.brand.default,
        },
      }),
    },
    {
      props: { color: 'secondary', variant: 'contained' },
      style: ({ theme }) => secondaryContainedButtonStyle(theme),
    },
    {
      props: { color: 'secondary', variant: 'text' },
      style: ({ theme }) => ({
        color: theme.palette.text.muted,
        '&:hover': {
          color: theme.palette.text.default,
          backgroundColor: theme.palette.bg.interactive.hover,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.text.default,
        },
      }),
    },
    {
      props: { color: 'error', variant: 'contained' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.danger.default,
        color: theme.palette.text.danger.onDanger,
        '&:hover': {
          backgroundColor: theme.palette.bg.danger.hover,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          backgroundColor: theme.palette.bg.danger.default,
          color: theme.palette.text.danger.onDanger,
        },
      }),
    },
    {
      props: { color: 'error', variant: 'text' },
      style: ({ theme }) => ({
        color: theme.palette.text.danger.default,
        '&:hover': {
          color: theme.palette.text.danger.onDangerMuted,
          backgroundColor: theme.palette.bg.danger.muted,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.text.danger.onDangerMuted,
        },
      }),
    },
    // Size
    {
      props: { size: 'large' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-lg'],
        padding: theme.spacing(2, 4),
      }),
    },
    {
      props: { size: 'medium' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-lg'],
        padding: theme.spacing(1.5, 3),
      }),
    },
    {
      props: { size: 'small' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-md'],
        padding: theme.spacing(0.5, 1.5),
      }),
    },
  ],
}
